import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClock, faMapMarkerAlt, faTimes, faUsers } from '@fortawesome/free-solid-svg-icons/index';
import { employerJobCreationStepsSingle, privatePartyJobCategoryTitle } from '../../../../constants/jobs';
import { alertMessages, errorMinJobHoursMessage } from '../../../../constants/text';
import { errorCodes } from '../../../../constants/errorCodes';
import { useTimezoneForDate } from '../../../../helpers/timezones';
import { getCostPayRate, getEstCost, getDollarPrice } from '../../../../helpers/general';
import { modifyObject } from '../../../../helpers/modifyObject';
import { getTimeInfoUi, resolveDateTime } from '../../../../helpers/jobs';
import { appPath } from '../../../../constants';
import LocationDateStep from './LocationDateStep';
import SkillQuantityStep from './SkillQuantityStep';
import DressCodeDescriptionStep from './DressCodeDescriptionStep';
import Summary from './Summary';
import { uiOnlyDateFormat } from '../../../../constants/date';
import UsersService from '../../../../services/UsersService';

export default class CreateJobEmployerSingle extends Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    getCompanyLocations: PropTypes.func.isRequired,
    companyLocations: PropTypes.array.isRequired,
    locationsList: PropTypes.array.isRequired,
    jobCategories: PropTypes.array.isRequired,
    basicSkillLevelId: PropTypes.number,
    costPayRateList: PropTypes.array.isRequired,
    costPayRateIsLoading: PropTypes.bool.isRequired,
    dressCodesList: PropTypes.array.isRequired,
    addUserLocation: PropTypes.func.isRequired,
    getAllJobCategories: PropTypes.func.isRequired,
    getPayCostRateAction: PropTypes.func.isRequired,
    createJobAction: PropTypes.func.isRequired,
    postToBoardAction: PropTypes.func.isRequired
  };

  state = {
    currentStep: employerJobCreationStepsSingle.first.id,
    selectedLocation: null,
    startDate: null,
    startTime: null,
    endTime: null,
    hours: '',
    selectedCategoryId: null,
    jobSkillData: null,
    quantity_of_workers: '',
    job_cost_rate: 0,
    worker_pay_rate: 0,
    checkin_verification_required: false,
    dress_code_id: 0,
    dress_code: '',
    description: '',
    where_to_report: '',
    validationErrors: {
      hours: null
    },
    isLoading: false
  };

  componentDidMount () {
    this.props.getAllJobCategories();

    this.props.getCompanyDetailsAction();

    this.props.getCompanyLocations(this.props.user.company_id).then(() => {
      if (this.props.companyLocations.length === 1) {
        const locationData = this.props.companyLocations[0];
        const selectedLocation = {
          label: `${locationData.location_name}, ${locationData.address}`,
          value: locationData.id,
          data: locationData
        };
        this.handleSelectLocation(selectedLocation);
      }
    });
  }

  componentDidUpdate (prevProps) {
    const { companyDetails, jobCategories } = this.props;
    if (companyDetails.is_individual && jobCategories.length > 0 && !this.state.selectedCategoryId) {
      const privatePartyCategory = jobCategories.find(category => category.reference_title === privatePartyJobCategoryTitle);
      if (privatePartyCategory) this.chooseCategory(privatePartyCategory);
    }
  }

  handleSwitch = name => this.setState(prevState => ({ [name]: !prevState[name] }));

  exitJobCreation = () =>
    this.props.UIShowConfirm(
      'Are you sure you want to cancel? Your job will not be saved.',
      () => this.props.history.push(`/${appPath}/jobs`)
    );

  goToStep = stepId => {
    this.setState({
      currentStep: stepId
    });
  };

  handleSelectLocation = selectedLocation => {
    if (selectedLocation) {
      const { address, city, state, zip, coordinates } = selectedLocation.data;
      if (!address || !city || !state || !zip || !coordinates) {
        return this.props.UIShowAlert('Sorry, the address is invalid.');
      }
    }
    this.setState({ selectedLocation }, () => this.setCostPayRate());
  };

  addLocationToJob = locationData => {
    return this.props.addUserLocation(locationData).then(() => {
      this.setState({
        selectedLocation: {
          label: `${locationData.location_name}, ${locationData.address}`,
          value: locationData.id,
          data: locationData
        }
      }, () => this.setCostPayRate());
    });
  };

  handleChangeStartDate = startDate => {
    this.setState({ startDate }, () => { this.setDateTimeHours(); });
  };

  handleChangeStartTime = startTime => {
    this.handleChangeTime('startTime', startTime);
  };

  handleChangeEndTime = endTime => {
    this.handleChangeTime('endTime', endTime);
  };

  handleChangeTime = (name, value) => {
    if (!value) {
      this.resetHoursValue();
    }
    this.setState({
      [name]: value
    }, () => { this.setDateTimeHours(); });
  };

  setDateTimeHours = () => {
    let { startDate, startTime, endTime } = this.state;
    if (startDate && startTime && endTime) {
      const updatedData = resolveDateTime(startDate, startTime, endTime);
      this.setState(prevState => ({
        startTime: updatedData.startTime,
        endTime: updatedData.endTime,
        hours: updatedData.hours,
        validationErrors: {
          ...prevState.validationErrors,
          hours: updatedData.hoursErrorMessage
        }
      }));
    }
  };

  resetHoursValue = () => {
    this.setState(prevState => ({
      hours: 0,
      validationErrors: {
        ...prevState.validationErrors,
        hours: errorMinJobHoursMessage
      }
    }));
  };

  chooseCategory = selectedCategoryData => {
    this.setState({
      selectedCategoryId: selectedCategoryData.id
    }, () => this.handleChangeSkill(null));
  };

  handleChangeSkill = jobSkillData => {
    const jobDescription = jobSkillData && jobSkillData.id ? jobSkillData.job_comments_template : '';
    this.setState({
      jobSkillData,
      description: jobDescription
    }, () => this.setCostPayRate());
  };

  setCostPayRate = () => {
    const { jobSkillData, selectedLocation } = this.state;
    const { user } = this.props;
    if (jobSkillData && jobSkillData.id && selectedLocation) {
      const data = {
        job_skill_id: jobSkillData.id,
        job_coordinates: selectedLocation.data.coordinates,
        state: selectedLocation.data.state,
        company_id: user.company_id
      };
      const { getPayCostRateAction, basicSkillLevelId } = this.props;
      getPayCostRateAction(data).then(costPayRateList => {
        if (costPayRateList) {
          this.setState({
            job_cost_rate: getCostPayRate('cost_rate', costPayRateList, basicSkillLevelId),
            worker_pay_rate: getCostPayRate('pay_rate', costPayRateList, basicSkillLevelId)
          });
        }
      });
    }
  };

  handleChangeWorkersQuantity = event => {
    const { value } = event.target;
    const quantity_of_workers = value === '' ? value : parseInt(value, 10);
    this.setState({ quantity_of_workers });
  };

  handleChangeDressCode = dressCodeData => {
    this.setState({ dress_code_id: dressCodeData.id });
  };

  handleChangeTextInput = event => {
    const { name, value } = event.target;
    this.setState({
      [name]: value
    });
  };

  isFirstStepFilled = () => {
    const { selectedLocation, hours, validationErrors } = this.state;
    return selectedLocation && !!selectedLocation.value && !!hours && !validationErrors.hours;
  };

  isSecondStepFilled = () => {
    const { jobSkillData, quantity_of_workers } = this.state;
    return jobSkillData && !!jobSkillData.id && !!quantity_of_workers;
  };

  isThirdStepFilled = () => {
    return !!this.state.description;
  };

  getStepName = () => {
    const { currentStep } = this.state;
    switch (currentStep) {
      case employerJobCreationStepsSingle.first.id:
        return employerJobCreationStepsSingle.first.ui;
      case employerJobCreationStepsSingle.second.id:
        return employerJobCreationStepsSingle.second.ui;
      case employerJobCreationStepsSingle.third.id:
        return employerJobCreationStepsSingle.third.ui;
      default:
        return '';
    }
  };

  calcEstCost = () => {
    const { job_cost_rate, hours, quantity_of_workers } = this.state;
    const jobCostRate = getDollarPrice(job_cost_rate);
    return getEstCost(jobCostRate, hours, quantity_of_workers);
  };

  createJob = () => {
    const { hours, quantity_of_workers, jobSkillData, dress_code_id, dress_code, description, where_to_report,
      job_cost_rate, worker_pay_rate, selectedLocation, startTime, endTime, checkin_verification_required } = this.state;
    const { address, state, city, zip, coordinates, location_name, job_location_type_id, time_zone_id } = selectedLocation.data;
    const start_date = useTimezoneForDate(startTime, time_zone_id);
    const end_date = useTimezoneForDate(endTime, time_zone_id);

    const jobData = modifyObject({
      user_id: this.props.user.userid,
      start_date,
      end_date,
      hours,
      is_estimated_hours: true,
      job_skill_id: jobSkillData.id,
      quantity_of_workers,
      skill_level_id: this.props.basicSkillLevelId,
      dress_code_id,
      dress_code,
      description,
      where_to_report,
      job_cost_rate,
      worker_pay_rate,
      address,
      state,
      city,
      zip,
      address_coordinates: coordinates,
      location_name,
      job_location_type_id,
      checkin_verification_required
    });
    this.setState({
      isLoading: true
    });
    return this.props.createJobAction(jobData);
  };

  saveJob = () => {
    this.createJob().then(() => {
      this.props.history.push(`/${appPath}/jobs`);
    });
  };

  postJobToBoard = () => {
    this.createJob().then(({ id }) => {
      this.postJob(id, true);
    });
  };

  letChooseWorker = () => {
    this.createJob().then(({ id }) => {
      this.postJob(id, false);
    });
  };

  postJob = (jobId, postToBoard) => {
    const { postToBoardAction } = this.props;
    postToBoardAction([jobId], postToBoard).then(data => {
      if (data.error && data.error.code === errorCodes.noCConFile) {
        this.props.history.push(`/${appPath}/jobs`);
        return this.props.UIShowAlert(alertMessages.noCConFileJobCreation[UsersService.getUserRole()]);
      } else {
        if (postToBoard) {
          this.props.history.push(`/${appPath}/jobs`);
        } else {
          this.props.history.push(`/${appPath}/jobs/${jobId}`, { switchToWorkers: true, cantReturnBack: true });
        }
      }
    });
  };

  renderSteps = () => {
    const { currentStep, selectedLocation, startDate, startTime, endTime, hours, quantity_of_workers, selectedCategoryId,
      jobSkillData, dress_code_id, dress_code, description, where_to_report, validationErrors, isLoading, checkin_verification_required } = this.state;
    const jobSkillId = jobSkillData ? jobSkillData.id : null;
    const dressCodeLabel = dress_code_id ? this.props.dressCodesList.find(listItem => listItem.id === dress_code_id).description_en : '';
    switch (currentStep) {
      case employerJobCreationStepsSingle.first.id:
        return (
          <LocationDateStep
            selectedLocation={selectedLocation}
            companyLocations={this.props.companyLocations}
            locationsList={this.props.locationsList}
            handleSelectLocation={this.handleSelectLocation}
            addLocationToJob={this.addLocationToJob}
            startDate={startDate}
            handleChangeStartDate={this.handleChangeStartDate}
            startTime={startTime}
            endTime={endTime}
            handleChangeStartTime={this.handleChangeStartTime}
            handleChangeEndTime={this.handleChangeEndTime}
            isFirstStepFilled={this.isFirstStepFilled}
            goToStep={this.goToStep}
            validationErrors={validationErrors}
          />
        );
      case employerJobCreationStepsSingle.second.id:
        return (
          <SkillQuantityStep
            use_custom_rate={this.props.companyDetails.use_custom_rate}
            jobCategories={this.props.jobCategories}
            selectedCategoryId={selectedCategoryId}
            jobSkillId={jobSkillId}
            chooseCategory={this.chooseCategory}
            handleChangeSkill={this.handleChangeSkill}
            quantityOfWorkers={quantity_of_workers}
            handleChangeWorkersQuantity={this.handleChangeWorkersQuantity}
            estCost={this.calcEstCost()}
            costPayRateList={this.props.costPayRateList}
            costPayRateIsLoading={this.props.costPayRateIsLoading}
            isPrivateParty={this.props.companyDetails.is_individual}
            isSecondStepFilled={this.isSecondStepFilled}
            goToStep={this.goToStep}
          />
        );
      case employerJobCreationStepsSingle.third.id:
        return (
          <DressCodeDescriptionStep
            company={this.props.companyDetails}
            dressCodesList={this.props.dressCodesList}
            dressCodeId={dress_code_id}
            dressCode={dress_code}
            description={description}
            whereToReport={where_to_report}
            checkin_verification_required={checkin_verification_required}
            handleSwitch={this.handleSwitch}
            handleChangeDressCode={this.handleChangeDressCode}
            handleChangeTextInput={this.handleChangeTextInput}
            isThirdStepFilled={this.isThirdStepFilled}
            goToStep={this.goToStep}
          />
        );
      default:
        return (
          <Summary
            skillLabel={jobSkillData.skill_label}
            workersQuantity={quantity_of_workers}
            estCost={this.calcEstCost()}
            locationLabel={selectedLocation.label}
            startDate={startDate}
            startTime={startTime}
            endTime={endTime}
            actualHours={hours}
            dressCodeLabel={dressCodeLabel}
            dressCodeInstructions={dress_code}
            jobDescription={description}
            whereToReport={where_to_report}
            goToStep={this.goToStep}
            saveJob={this.saveJob}
            postJobToBoard={this.postJobToBoard}
            letChooseWorker={this.letChooseWorker}
            isLoading={isLoading}
          />
        );
    }
  };

  render () {
    const { currentStep, selectedLocation, startDate, startTime, endTime, hours, jobSkillData, quantity_of_workers, description } = this.state;
    const isShortSummaryShown = this.isFirstStepFilled() && currentStep !== employerJobCreationStepsSingle.summary.id;

    return (
      <div className='create-job-employer-single'>
        <div className='top-wrapper'>
          <h3>Post a Job</h3>
          <button
            className='close-button'
            onClick={this.exitJobCreation}
          ><FontAwesomeIcon icon={faTimes} size='xs' /></button>
        </div>
        { this.getStepName() && (
          <div className='tabs-wrapper'>
            <div className='tab active'>{ this.getStepName() }</div>
          </div>
        )}
        <div className='columns-wrapper'>
          <div className='steps-first-column'>
            { this.renderSteps() }
          </div>
          <section className='job-details-sum-main-wrapper'>
            { isShortSummaryShown && (
              <div className='job-details-sum-wrapper'>
                { this.isSecondStepFilled() && (
                  <div className='skill-cost-info'>
                    <div className='header-left'>
                      <div
                        className='job-image'
                        style={{ backgroundImage: `url('${jobSkillData.icon_url}')` }}
                      />
                      <h5 className='job-title'>{jobSkillData.skill_label}</h5>
                    </div>
                    <div className='header-right'>
                      <h5 className='est-cost-item'>{this.calcEstCost()}</h5>
                      <p className='text-muted mr-0'>Est. Pay</p>
                    </div>
                  </div>
                )}
                { this.isThirdStepFilled() && (
                  <p className='job-description'>{description}</p>
                )}
                <div className='job-details-sum'>
                  <p className='details-item start-date'>
                    <span className='icon-wrapper'><FontAwesomeIcon icon={faCalendarAlt} /></span>
                    { moment(startDate).format(uiOnlyDateFormat)}
                  </p>
                  <p className='details-item'>
                    <span className='icon-wrapper'><FontAwesomeIcon icon={faClock} /></span>
                    { getTimeInfoUi(startTime, endTime, hours) }
                  </p>
                  <p title={selectedLocation.label} className='details-item full-width'>
                    <span className='icon-wrapper'><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                    {selectedLocation.label}
                  </p>
                  { quantity_of_workers && (
                    <p className='details-item'>
                      <span className='icon-wrapper'><FontAwesomeIcon icon={faUsers} /></span>
                      {quantity_of_workers} Workers
                    </p>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/createJob/createJobEmployerSingle/index.js